import YmalOfferTileSmall from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/Small/YmalOfferTileSmall'
import { getTileViewProps } from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/Common/getTileViewProps'
import React, { useMemo, useEffect, useContext } from 'react'
import CalendarRecommendationsNotAvailableMessage from './CalendarRecommendationsNotAvailableMessage'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchCalendarRecommendations } from 'actions/RecommendationActions'
import moment from 'moment'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import CalendarPickerGridLayout from './CalendarPickerGridLayout'
import CalendarPickerRecommendationsLoadingIndicator from './CalendarPickerRecommendationsLoadingIndicator'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDistanceUnit } from 'selectors/recommendations'
import Heading from 'components/Luxkit/Typography/Heading'
import { YmalPricingProperties } from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/Common/types'
import { GAProductFieldObject } from 'analytics/eventDefinitions'
import { getListName } from 'analytics/enhanced-ecommerce'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import { calcDistanceBetweenOffers } from 'lib/offer/offerUtils'
import { DistanceUnit } from 'lib/geo/distanceUnits'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useOffersAvailableRates } from 'hooks/useOfferAvailableRates'
import useOffers from 'hooks/Offers/useOffers'
import CurrencyContext from 'contexts/currencyContext'
import useOfferList from 'hooks/Offers/useOfferList'
import { LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'
import Group from 'components/utils/Group'
import { monthIndexToMonth } from 'api/recommendations'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

interface Props {
  checkIn: string;
  monthIndex: string;
  duration?: number;
  distanceUnit: DistanceUnit;
  graphCalendarLoadingState: App.ApiResponseState;
  graphCalendarOfferIds: Array<string>;
  offerType: App.OfferType;
  offerId: string;
  rooms?: Array<App.Occupants>;
  heading?: string;
  seedOffer?: App.Offer | App.BedbankOffer;
  defaultDepositAmountPercentage: number;
}

function mapProductClickGAObject(offer: App.Offer | App.OfferSummary | App.BedbankOffer, pricing: YmalPricingProperties, position: number): GAProductFieldObject {
  return {
    id: offer.id,
    name: offer.property?.name ?? offer.name,
    category: offer.analyticsType,
    price: pricing?.price,
    memberPrice: pricing?.memberPrice,
    quantity: 1,
    position,
  }
}

function getLereCalendarTrackingListName(offerType: App.OfferType) {
  const mapping: { [key in App.OfferType]?: string } = {
    hotel: 'FLASH',
    tactical_ao_hotel: 'LPC',
    bedbank_hotel: 'LPP',
  }
  const offerTypeLabel = mapping[offerType] ?? offerType
  return getListName('LERE', offerTypeLabel, 'Calendar V2.0.0')
}

const MAX_DISPLAY_COUNT = 6

function CalendarPickerRecommendations({
  checkIn,
  duration,
  distanceUnit,
  graphCalendarLoadingState,
  monthIndex,
  graphCalendarOfferIds = EmptyArray,
  offerType,
  offerId,
  rooms,
  heading,
  seedOffer,
  defaultDepositAmountPercentage,
}: Props) {
  const currency = useContext(CurrencyContext)
  const reduxDispatch = useAppDispatch()
  const checkInISO = useMemo(() => moment(checkIn).format(ISO_DATE_FORMAT), [checkIn])
  const checkOutISO = useMemo(() => moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT), [checkIn, duration])
  const occupancy = rooms?.length ? rooms : initialRooms

  const isLoading = graphCalendarLoadingState === 'loading'
  const offerIds = useMemo(() => !isLoading ? take(graphCalendarOfferIds, MAX_DISPLAY_COUNT) : EmptyArray, [graphCalendarOfferIds, isLoading])
  const useHomepage = !isLoading && offerIds.length === 0
  const homepageOffers = useOfferList({
    offerTypes: LE_CONTRACTED_HOTEL_TYPES,
    limit: 6,
    isLight: true,
  }, { disabled: !useHomepage })
  const final: Array<string> = useMemo(() => [...offerIds, ...homepageOffers.offerIds], [offerIds, homepageOffers])

  const [offers, fetchingOffers] = useOffers<App.HotelOffer>(final)
  const offerAvailableRates = useOffersAvailableRates(final, {
    checkIn: !useHomepage ? checkInISO : undefined,
    checkOut: !useHomepage ? checkOutISO : undefined,
    rooms: occupancy,
  })

  const recommendationsHeader = useMemo(() => {
    if (!offerIds.length) {
      return 'Other offers you may like'
    }
    return heading
  }, [offerIds.length, heading])

  useEffect(() => {
    if ((checkIn || monthIndex) && occupancy && duration) {
      reduxDispatch(fetchCalendarRecommendations(offerId, {
        rooms: occupancy,
        checkIn,
        availableMonth: monthIndex ? moment(monthIndexToMonth(monthIndex), 'M-YYYY').format('YYYY-MM') : undefined,
        duration,
      }))
    }
  }, [checkIn, monthIndex, duration, occupancy, offerId, reduxDispatch])

  const tracking = useOfferListTracking(
    offerIds.length ? getLereCalendarTrackingListName(offerType) : 'calendar - homepage - recommendations',
  )

  const linkQuery = useMemo(() => buildSearchStringFromFilters({
    propertyId: offerId,
    searchNearby: true,
    checkIn: checkInISO,
    checkOut: checkOutISO,
    rooms: occupancy,
  }), [occupancy, checkInISO, checkOutISO, offerId])

  const offerDistances = useMemo(() => {
    const offerIdDistances: Record<string, number | null> = {}
    if (!seedOffer) {
      return offerIdDistances
    }
    offers.forEach((offer) => {
      offerIdDistances[offer.id] = calcDistanceBetweenOffers(offer, seedOffer)
    })
    return offerIdDistances
  }, [offers, seedOffer])

  // only fetch best prices if we are using exact dates (no monthIndex)
  const selectedDatesAndRoomsFilter: App.OfferListFilters = useMemo(() => ({
    checkIn: monthIndex ? undefined : checkInISO,
    checkOut: monthIndex ? undefined : checkOutISO,
    rooms: monthIndex ? undefined : occupancy,
  }), [monthIndex, checkInISO, checkOutISO, occupancy])

  const bestPrices = useOffersBestPrices(offers, selectedDatesAndRoomsFilter)

  // #endregion

  if (isLoading || fetchingOffers || homepageOffers.fetching) {
    return (
      <CalendarPickerRecommendationsLoadingIndicator rooms={rooms} />
    )
  }

  if (!offers.length) {
    return (
      <CalendarRecommendationsNotAvailableMessage />
    )
  }

  return (
    <Group gap={8} direction="vertical">
      {heading && <Heading className="heading" variant="heading6">{recommendationsHeader}</Heading>}
      <OfferListEventsProvider tracking={tracking}>
        <CalendarPickerGridLayout>
          {offers.map((offer, i) => {
            const viewProperties = getTileViewProps(offer, {
              currency,
              offerAvailableRates: offerAvailableRates?.[offer.id],
              distance: offerDistances?.[offer.id],
              distanceUnit,
              distanceFrom: seedOffer?.property?.name,
              duration: useHomepage ? undefined : duration,
              defaultDepositAmountPercentage,
            })
            if (!viewProperties) {
              return null
            }

            return (
              <YmalOfferTileSmall
                anchorTarget="_blank"
                key={offer.id}
                offer={offer}
                linkQuery={linkQuery}
                currencyCode={currency}
                duration={viewProperties.duration}
                depositAmount={viewProperties.depositAmount}
                depositAmountPercentage={viewProperties.depositAmountPercentage}
                depositEnabled={viewProperties.depositEnabled}
                depositThresholds={viewProperties.depositThresholds}
                tourDepositType={viewProperties.tourDepositType}
                distance={viewProperties.distance}
                distanceFrom={viewProperties.distanceFrom}
                distanceUnit={viewProperties.distanceUnit}
                pricing={viewProperties.pricing}
                position={i}
                testId="lere-calendar-tile-small"
                productClickObjectMapper={mapProductClickGAObject}
                alternativeCheckIn={monthIndex ? undefined : checkInISO}
                alternativeCheckOut={monthIndex ? undefined : checkOutISO}
                alternativePrice={monthIndex ? undefined : bestPrices[offer.id]?.bestPrice}
              />
            )
          })}
        </CalendarPickerGridLayout>
      </OfferListEventsProvider>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    checkIn: state.recommendationsCheckInDay,
    distanceUnit: getDistanceUnit(appState),
    graphCalendarOfferIds: appState.recommendations.calendarRecommendations.offerIds,
    graphCalendarLoadingState: appState.recommendations.calendarRecommendations.state,
    monthIndex: state.recommendationsMonthIndex,
    duration: state.duration,
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  }
}, CalendarPickerRecommendations)
